// Tournaments (contracts/tournaments.md §9; backend/api src/schemas/cups.ts and routes/admin/cups.ts): cups,
// their entries, results confirmation, a live final's state, the prize queue and the cup puzzle pools (each
// week's pools, their sets, one set's puzzles with answers and who was dealt it). Every role reads; editors
// write; confirming results is admin. The cups.* settings go through GET/PUT/PATCH /config. Kept apart from
// client.ts / queries.ts like the other feature modules.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { AWARD_STATUSES, type Bracket, CUP_KINDS, CUP_STATUSES, type CupKind, type CupStatus, type Prize, PRIZE_KINDS } from "../lib/cups";
import { request } from "./client";
import { LOCALES } from "./messaging";

const count = z.number().int().min(0);
const loose = z.record(z.string(), z.unknown());

export const CupContent = z.partialRecord(
  z.enum(LOCALES),
  z.object({ title: z.string().optional(), subtitle: z.string().optional(), rules: z.string().optional(), prizeHeadline: z.string().optional() }),
);
export type CupContent = z.infer<typeof CupContent>;

const ApiBracket = z.object({ id: z.string(), name: z.string().optional(), minLevel: z.number().int(), maxLevel: z.number().int().nullable().optional(), pool: z.string() });
export const Qualifying = z.object({ month: z.string(), bestOf: z.number().int(), perBracket: z.number().int() });
export type Qualifying = z.infer<typeof Qualifying>;
/** The stored final: `{ mode, liveAt? }`, plus `registeredAt` / `resultsAt` once the realtime service has it. */
export const Final = z.object({ mode: z.enum(["async", "live"]), liveAt: z.string().optional() }).loose();
export type Final = z.infer<typeof Final>;

/** AdminCupList items and AdminCup share these (the API's AdminCupBase). */
const CupBase = z.object({
  id: z.string(),
  kind: z.enum(CUP_KINDS),
  number: z.number().int().nullable(),
  /** By the clock (scheduled → open → closed) unless draft, finalized or cancelled. */
  status: z.enum(CUP_STATUSES),
  storedStatus: z.enum(CUP_STATUSES),
  opensAt: z.string(),
  closesAt: z.string(),
  week: z.string(),
  content: loose.transform((c) => CupContent.catch({}).parse(c)),
  /** The English title with `{number}` filled in. */
  title: z.string(),
  bannerImageId: z.string().nullable(),
  brackets: z.array(ApiBracket),
  setSize: z.number().int(),
  qualifying: Qualifying.nullable(),
  final: Final.nullable(),
  entrants: count,
  /** Hidden from players: not listed in the app, can't be entered, no pushes; its entrants keep it. */
  hidden: z.boolean().default(false),
  confirmedAt: z.string().nullable(),
  finalizedAt: z.string().nullable(),
  qualifiedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const AdminCupListItem = CupBase.extend({ finished: count, flagged: count.meta({ description: "Entries with flags, or disqualified." }) });
export type AdminCupListItem = z.infer<typeof AdminCupListItem>;
export const AdminCupList = z.object({ items: z.array(AdminCupListItem) });

export const AdminPrize = z.object({
  id: z.string(),
  bracket: z.string(),
  country: z.string().nullable(),
  rankFrom: z.number().int(),
  rankTo: z.number().int(),
  kind: z.enum(PRIZE_KINDS),
  value: z.string(),
  content: loose.transform((c) => (c as Prize["content"]) ?? {}),
  giftCards: z.array(z.string()),
  minEntrants: z.number().int(),
});
export type AdminPrize = z.infer<typeof AdminPrize>;

/** GET /admin/v1/cups/{id}: the cup with its prizes, award counts per status and qualifiers per bracket. */
export const AdminCup = CupBase.extend({
  bannerUrl: z.string().nullable(),
  prizes: z.array(AdminPrize),
  awards: z.record(z.string(), count),
  qualifiers: z.record(z.string(), count),
});
export type AdminCup = z.infer<typeof AdminCup>;

/** POST /cups (CupRequest). `liveAt` is left out of an async final; qualifying and final only on a Championship. */
export interface CupDraft {
  kind: CupKind;
  opensAt: string;
  closesAt: string;
  content: CupContent;
  bannerImageId: string | null;
  brackets: Bracket[];
  setSize: number;
  prizes: Prize[];
  qualifying: Qualifying | null;
  final: { mode: "async" | "live"; liveAt?: string } | null;
  /** draft (hidden from players) or scheduled (the default). */
  status?: "draft" | "scheduled";
  /** Hidden from players (default false); changes any time, a finalized or cancelled cup's too (alone). */
  hidden?: boolean;
}
/** PATCH /cups/{id} (CupPatchRequest): no kind; `status: "scheduled"` publishes a draft. */
export type CupPatch = Partial<Omit<CupDraft, "kind" | "status">> & { status?: "scheduled" };

export const CupEntry = z.object({
  userId: z.string(),
  handle: z.string().nullable(),
  bracket: z.string(),
  country: z.string().nullable(),
  playUp: z.boolean(),
  enteredAt: z.string(),
  played: count,
  finishedAt: z.string().nullable(),
  /** 0–100 (points ÷ 100), as players see it. */
  score: count,
  /** Σ puzzle scores (up to 1 000 a puzzle). */
  points: count,
  timeMs: count,
  rank: z.number().int().nullable(),
  countryRank: z.number().int().nullable(),
  livePlacement: z.number().int().nullable(),
  status: z.enum(["active", "disqualified"]),
  dqReason: z.string().nullable(),
  /** From set play: too_fast, impossible_timing. */
  flags: z.array(z.string()),
  /** Apple or Google sign-in linked (needed for Pro days and money prizes). */
  linked: z.boolean(),
  /** The cup set this entrant was dealt (GET /cups/sets/{setId}); null before one is dealt. */
  setId: z.string().nullable(),
  /** Other entrants of this cup sharing an install or a push token. */
  duplicates: z.array(z.string()),
});
export type CupEntry = z.infer<typeof CupEntry>;
export const CupEntries = z.object({ items: z.array(CupEntry) });

/** GET /cups/{id}/live: `{ live: false }` unless the cup has a live final. */
export const LiveFinal = z.object({
  live: z.boolean(),
  liveAt: z.string().nullable().optional(),
  registeredAt: z.string().nullable().optional(),
  brackets: z
    .array(
      z.object({
        id: z.string(),
        dropId: z.string(),
        qualifiers: count,
        resultsAt: z.string().nullable(),
        /** The realtime service's state (scheduled, lobby, running, finished, not_enough_players, interrupted); null when unknown. */
        state: z.string().nullable().optional(),
        /** Players in the lobby now; null when unknown. */
        lobby: count.nullable().optional(),
      }),
    )
    .optional(),
});
export type LiveFinal = z.infer<typeof LiveFinal>;

export const Award = z.object({
  id: z.string(),
  cupId: z.string(),
  /** With the Weekly Cup's number filled in. */
  cupTitle: z.string(),
  userId: z.string().nullable(),
  handle: z.string().nullable(),
  rank: z.number().int(),
  bracket: z.string(),
  country: z.string().nullable(),
  kind: z.enum(PRIZE_KINDS),
  value: z.string(),
  label: z.string(),
  status: z.enum(AWARD_STATUSES),
  /** What the player confirmed when claiming. */
  claim: z.object({ adult: z.boolean(), country: z.string(), giftCard: z.string().nullable(), at: z.string() }).nullable(),
  adminNote: z.string().nullable(),
  /** The claim deadline once results are confirmed (confirmation + cups.claimDays). */
  claimBy: z.string().nullable(),
  /** Claimed more than 3 days ago and not sent. */
  overdue: z.boolean(),
  confirmed: z.boolean(),
  claimedAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  doneAt: z.string().nullable(),
  createdAt: z.string(),
});
export type Award = z.infer<typeof Award>;
export const Awards = z.object({ items: z.array(Award) });
export type AwardMove = "sent" | "done" | "void";

// ---------------------------------------------------------------- cup puzzles

// Every entrant plays their own set of 10 from their bracket's pool (cup.rookie, cup.challenger, cup.master) of
// the cup's ISO week; the pools are seeded with the weekly sets (contracts/tournaments.md §2).

/** GET /cups/pools?week=: the seeded weeks, the chosen week's pools (calibration and use) and that week's cups. */
export const CupPools = z.object({
  weeks: z.array(z.object({ week: z.string(), generator: z.string(), seededAt: z.string() })),
  /** The week asked for, else the first seeded week from this one on (else the last); null when nothing is seeded. */
  week: z.string().nullable(),
  pools: z.array(
    z.object({
      pool: z.string(),
      /** Its sets are k = first … first + count − 1. */
      first: z.number().int(),
      count: z.number().int(),
      targetMean: z.number().nullable(),
      targetSpread: z.number().nullable(),
      /** Sets dealt from this pool this week (one per entrant and cup). */
      players: count,
      /** Distinct sets dealt. */
      setsInUse: count,
    }),
  ),
  cups: z.array(z.object({ id: z.string(), kind: z.enum(CUP_KINDS), number: z.number().int().nullable(), title: z.string(), status: z.enum(CUP_STATUSES), opensAt: z.string() })),
});
export type CupPools = z.infer<typeof CupPools>;
export type CupPool = CupPools["pools"][number];

export const CupSetType = z.object({ type: z.string(), category: z.string(), difficulty: z.number() });
export type CupSetType = z.infer<typeof CupSetType>;

/** GET /cups/pools/{week}/{pool}?page=&used=: 50 sets a page by k, each with its 10 types and how many hold it. */
export const CupPoolSets = z.object({
  week: z.string(),
  pool: z.string(),
  total: count,
  page: z.number().int(),
  sets: z.array(
    z.object({
      setId: z.string(),
      k: z.number().int(),
      mean: z.number(),
      spread: z.number(),
      /** Players dealt this set (any cup of the week). */
      players: count,
      types: z.array(CupSetType),
    }),
  ),
});
export type CupPoolSets = z.infer<typeof CupPoolSets>;
export type CupPoolSet = CupPoolSets["sets"][number];
export const CUP_POOL_PAGE = 50;

/** GET /cups/sets/{setId}: every puzzle with its spec, answer and explanation, and who was dealt the set. */
export const CupSet = z.object({
  setId: z.string(),
  week: z.string(),
  pool: z.string().nullable(),
  k: z.number().int(),
  mean: z.number(),
  spread: z.number(),
  puzzles: z.array(
    z.object({
      index: z.number().int(),
      specId: z.string(),
      type: z.string(),
      category: z.string(),
      difficulty: z.number(),
      /** A PuzzleSpec and its answer as stored (parsed for the renderer by the page). */
      spec: z.unknown(),
      answer: z.unknown(),
      explanation: z.string(),
    }),
  ),
  holders: z.array(
    z.object({
      userId: z.string(),
      handle: z.string().nullable(),
      cupId: z.string(),
      /** Null when the cup is gone. */
      cupTitle: z.string().nullable(),
      /** The entry's (null without one). */
      bracket: z.string().nullable(),
      country: z.string().nullable(),
      assignedAt: z.string(),
      played: count,
      /** 0–100, as players see it. */
      score: count.nullable(),
      rank: z.number().int().nullable(),
      status: z.enum(["active", "disqualified"]).nullable(),
    }),
  ),
});
export type CupSet = z.infer<typeof CupSet>;
export type CupSetHolder = CupSet["holders"][number];

const Ok = z.object({ ok: z.literal(true) });

// ---------------------------------------------------------------- client

const at = (id: string) => `/cups/${encodeURIComponent(id)}`;

export const cupsApi = {
  list: (q: { status?: CupStatus; kind?: CupKind } = {}, signal?: AbortSignal) => request(AdminCupList, "GET", "/cups", { query: q, signal }),
  get: (id: string, signal?: AbortSignal) => request(AdminCup, "GET", at(id), { signal }),
  create: (draft: CupDraft) => request(AdminCup, "POST", "/cups", { body: draft }),
  update: (id: string, patch: CupPatch) => request(AdminCup, "PATCH", at(id), { body: patch }),
  cancel: (id: string) => request(AdminCup, "POST", `${at(id)}/cancel`, { body: {} }),
  entries: (id: string, q: { bracket?: string; country?: string; flagged?: boolean } = {}, signal?: AbortSignal) =>
    request(CupEntries, "GET", `${at(id)}/entries`, { query: { bracket: q.bracket, country: q.country, flagged: q.flagged ? "true" : undefined }, signal }),
  disqualify: (id: string, userId: string, reason: string) => request(Ok, "POST", `${at(id)}/entries/${encodeURIComponent(userId)}/disqualify`, { body: { reason } }),
  reinstate: (id: string, userId: string) => request(Ok, "POST", `${at(id)}/entries/${encodeURIComponent(userId)}/reinstate`, { body: {} }),
  confirm: (id: string) => request(AdminCup, "POST", `${at(id)}/confirm`, { body: {} }),
  live: (id: string, signal?: AbortSignal) => request(LiveFinal, "GET", `${at(id)}/live`, { signal }),
  /** No status: prizes of money value in any status; a status: every kind in it (granted is XP, badges, Pro days). */
  awards: (status?: string, signal?: AbortSignal) => request(Awards, "GET", "/cups/awards", { query: { status }, signal }),
  updateAward: (id: string, patch: { status?: AwardMove; adminNote?: string | null }) => request(Award.nullable(), "PATCH", `/cups/awards/${encodeURIComponent(id)}`, { body: patch }),
  contact: (id: string) => request(z.object({ ok: z.literal(true), userId: z.string() }), "POST", `/cups/awards/${encodeURIComponent(id)}/contact`, { body: {} }),
  /** No week: the API picks this week if it is seeded, else the next seeded one (else the last). */
  pools: (week?: string, signal?: AbortSignal) => request(CupPools, "GET", "/cups/pools", { query: { week }, signal }),
  poolSets: (week: string, pool: string, q: { page?: number; used?: boolean; sort?: "k" | "players" } = {}, signal?: AbortSignal) =>
    request(CupPoolSets, "GET", `/cups/pools/${encodeURIComponent(week)}/${encodeURIComponent(pool)}`, {
      query: { page: q.page && q.page > 1 ? q.page : undefined, used: q.used ? "true" : undefined, sort: q.sort === "players" ? "players" : undefined },
      signal,
    }),
  set: (setId: string, signal?: AbortSignal) => request(CupSet, "GET", `/cups/sets/${encodeURIComponent(setId)}`, { signal }),
};

export const cupKeys = {
  all: ["cups"] as const,
  list: ["cups", "list"] as const,
  detail: (id: string) => ["cups", "detail", id] as const,
  entries: (id: string, q: object) => ["cups", "entries", id, q] as const,
  live: (id: string) => ["cups", "live", id] as const,
  awards: (status: string) => ["cups", "awards", status] as const,
  pools: (week: string | undefined) => ["cups", "pools", week ?? ""] as const,
  poolSets: (week: string, pool: string, q: object) => ["cups", "poolSets", week, pool, q] as const,
  set: (setId: string) => ["cups", "set", setId] as const,
};

/** Every cup; `poll` refreshes every 30 s. */
export const useCups = (poll = false) => useQuery({ queryKey: cupKeys.list, queryFn: ({ signal }) => cupsApi.list({}, signal), refetchInterval: poll ? 30_000 : false });
export const useCup = (id: string | undefined, poll = false) =>
  useQuery({ queryKey: cupKeys.detail(id ?? ""), queryFn: ({ signal }) => cupsApi.get(id!, signal), enabled: Boolean(id), refetchInterval: poll ? 30_000 : false });
export const useCupEntries = (id: string, q: { bracket?: string; country?: string; flagged?: boolean }, poll = false) =>
  useQuery({ queryKey: cupKeys.entries(id, q), queryFn: ({ signal }) => cupsApi.entries(id, q, signal), refetchInterval: poll ? 30_000 : false });
export const useLiveFinal = (id: string, enabled: boolean) =>
  useQuery({ queryKey: cupKeys.live(id), queryFn: ({ signal }) => cupsApi.live(id, signal), enabled, refetchInterval: 15_000 });
/** `money`: the default queue (prizes of money value, any status); else one status, every kind. */
export const useAwards = (status: string = "money", enabled = true) =>
  useQuery({ queryKey: cupKeys.awards(status), queryFn: ({ signal }) => cupsApi.awards(status === "money" ? undefined : status, signal), enabled });

/** Under cupKeys.all, so a disqualification or reinstatement shows on the set's holders too. */
export const useCupPools = (week: string | undefined) => useQuery({ queryKey: cupKeys.pools(week), queryFn: ({ signal }) => cupsApi.pools(week, signal), placeholderData: keepPreviousData });
export const useCupPoolSets = (week: string | null | undefined, pool: string | null | undefined, q: { page?: number; used?: boolean; sort?: "k" | "players" }) =>
  useQuery({
    queryKey: cupKeys.poolSets(week ?? "", pool ?? "", q),
    queryFn: ({ signal }) => cupsApi.poolSets(week!, pool!, q, signal),
    enabled: Boolean(week && pool),
    placeholderData: keepPreviousData,
  });
export const useCupSet = (setId: string) => useQuery({ queryKey: cupKeys.set(setId), queryFn: ({ signal }) => cupsApi.set(setId, signal), enabled: Boolean(setId) });

function useCupMutation<V, R>(fn: (v: V) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      // A cup that came back is its page's data at once, so it shows before the refetch lands.
      const cup = AdminCup.safeParse(r);
      if (cup.success) client.setQueryData(cupKeys.detail(cup.data.id), cup.data);
      void client.invalidateQueries({ queryKey: cupKeys.all });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export const useCreateCup = () => useCupMutation((d: CupDraft) => cupsApi.create(d));
export const useUpdateCup = () => useCupMutation(({ id, patch }: { id: string; patch: CupPatch }) => cupsApi.update(id, patch));
export const useCancelCup = () => useCupMutation((id: string) => cupsApi.cancel(id));
export const useDisqualify = () =>
  useCupMutation(({ id, userId, reason }: { id: string; userId: string; reason: string | null }) => (reason === null ? cupsApi.reinstate(id, userId) : cupsApi.disqualify(id, userId, reason)));
export const useConfirmCup = () => useCupMutation((id: string) => cupsApi.confirm(id));
export const useUpdateAward = () => useCupMutation(({ id, patch }: { id: string; patch: { status?: AwardMove; adminNote?: string | null } }) => cupsApi.updateAward(id, patch));
export const useContactWinner = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cupsApi.contact(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["support"] });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
};
